import { create } from 'zustand';
import type { CostCategory } from '@immoaktas/shared';
import { uploadDocument } from '../../../lib/upload';
import { errorMessage } from '../../../i18n';

/** What a receipt is uploaded for (Umlage-Assistent "Rechnung hochladen"); the review form starts with it. */
export type UploadHint = { category?: CostCategory; serviceFrom?: string; serviceTo?: string };
/** Context the global upload sheet starts with (property and period preselected, e.g. from the checklist). */
export type UploadContext = { propertyId?: string; periodId?: string; files?: File[]; hint?: UploadHint };

export type UploadState = 'waiting' | 'uploading' | 'processing' | 'analyzed' | 'duplicate' | 'error' | 'confirmed';
export type UploadItem = { key: string; name: string; size: number; mime: string; file: File; propertyId?: string; hint?: UploadHint; state: UploadState; progress: number; documentId?: string; error?: string; addedAt: number };

type Store = {
  open: boolean;
  ctx: UploadContext;
  items: UploadItem[];
  focus: string | null;
  show: (ctx?: UploadContext) => void;
  hide: () => void;
  add: (files: File[], propertyId?: string, hint?: UploadHint) => void;
  update: (key: string, patch: Partial<UploadItem>) => void;
  setFocus: (key: string | null) => void;
  /** Removes finished items (confirmed, duplicate, failed upload); running ones stay. */
  clearDone: () => void;
};

let seq = 0;
let pumping = false;

/**
 * Global upload sheet (design/BelegUpload). Any screen can open it; `UploadSheetHost` in the shell renders it.
 * Uploads run here, outside React, so closing the sheet does not cancel them ("Du kannst weiterarbeiten").
 */
export const useUploadSheet = create<Store>((set, get) => ({
  open: false,
  ctx: {},
  items: [],
  focus: null,
  show: (ctx = {}) => set({ open: true, ctx }),
  hide: () => set({ open: false }),
  add: (files, propertyId, hint) => {
    const now = Date.now();
    const added = files.map((file) => ({ key: `u${++seq}`, name: file.name, size: file.size, mime: file.type, file, propertyId, ...(hint ? { hint } : {}), state: 'waiting' as const, progress: 0, addedAt: now }));
    set((s) => ({ items: [...s.items, ...added], focus: s.focus && s.items.some((i) => i.key === s.focus && i.state !== 'confirmed') ? s.focus : added[0]?.key ?? s.focus }));
    void pump();
  },
  update: (key, patch) => set((s) => ({ items: s.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) })),
  setFocus: (focus) => set({ focus }),
  clearDone: () => set((s) => {
    const items = s.items.filter((i) => !['confirmed', 'duplicate'].includes(i.state) && !(i.state === 'error' && !i.documentId));
    return { items, focus: items.find((i) => i.key === s.focus)?.key ?? items[0]?.key ?? null };
  }),
}));

/** Uploads one file after the other (calm progress, no parallel bandwidth fight). */
async function pump() {
  if (pumping) return;
  pumping = true;
  try {
    for (;;) {
      const next = useUploadSheet.getState().items.find((i) => i.state === 'waiting');
      if (!next) break;
      const { update } = useUploadSheet.getState();
      update(next.key, { state: 'uploading', progress: 0 });
      try {
        const r = await uploadDocument(next.file, { propertyId: next.propertyId, uploadHint: next.hint, onProgress: (p) => update(next.key, { progress: p }) });
        update(next.key, { documentId: r.documentId, progress: 1, state: r.duplicate ? 'duplicate' : 'processing' });
      } catch (e) {
        update(next.key, { state: 'error', error: errorMessage(e) });
      }
    }
  } finally {
    pumping = false;
  }
}
